import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Upload, X, Copy, CheckCircle2, Loader2, ChevronDown, ArrowUp, Images } from "lucide-react";
import { UNDERLAG_I18N, HOURS, MINUTES } from "./underlagI18n";
import { computeHours, fmtH, nonWorkingReason, NON_WORKING_LABELS } from "./underlagHours";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyWorker = () => ({
  name: "",
  job: "",
  description: "",
  from_h: "07",
  from_m: "00",
  to_h: "15",
  to_m: "00",
  timetype: "Normal time",
  minus_lunch: false,
});

const emptyForm = () => ({
  language: "en",
  date: new Date().toISOString().slice(0, 10),
  address: "",
  customer: "",
  project_id: "",
  work_description: "",
  workers: [emptyWorker()],
  toll_crossings: "",
  parking: "",
  materials_used: false,
  materials_net_rolls: "",
  materials_net_ties: "",
  materials_plastic_rolls: "",
  materials_plastic_ties: "",
  attachments: [],
  customer_email: "",
  customer_first_name: "",
  customer_last_name: "",
  customer_not_present: false,
  worker_email: "",
  cc_email_1: "",
  cc_email_2: "",
});

/** Searchable dropdown for name/job fields */
function Combobox({ value, onChange, options, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [query, options]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full min-w-[120px] flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md text-left text-sm hover:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        data-testid={testid}
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400 italic">No matches</div>
            )}
            {filtered.map((opt) => (
              <button
                type="button"
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setQuery("");
                }}
                className={`w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-cyan-50 ${
                  opt === value ? "bg-cyan-100 font-semibold" : ""
                }`}
              >
                {opt}
              </button>
            ))}
            {query && !filtered.includes(query) && (
              <button
                type="button"
                onClick={() => {
                  onChange(query);
                  setOpen(false);
                  setQuery("");
                }}
                className="w-full text-left px-3 py-2 text-sm text-cyan-700 hover:bg-cyan-50 border-t border-gray-100"
              >
                + Use &quot;{query}&quot;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Searchable dropdown for Project selection — richer than Combobox (matches by address/customer/name). */
function ProjectSelect({ projects, onPick, placeholder, hint }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return projects;
    return projects.filter((p) =>
      [p.address, p.customer, p.customer_email, p.name].some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [query, projects]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-cyan-300 rounded-md text-left text-sm hover:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        data-testid="select-project"
      >
        <span className="text-slate-500">{placeholder}</span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søk etter adresse, kunde eller nummer..."
              className="w-full px-2 py-1.5 text-sm text-slate-900 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid="project-search-input"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-400 italic">Ingen treff</div>
            )}
            {filtered.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => {
                  onPick(p);
                  setOpen(false);
                  setQuery("");
                }}
                className="w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-cyan-50 border-b border-slate-50 last:border-0"
                data-testid={`project-option-${p.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{p.address}</span>
                  {p.name && (
                    <span className="text-[10px] bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded font-mono">{p.name}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {p.customer}{p.customer_email ? ` — ${p.customer_email}` : ""}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

export default function UnderlagForm() {
  const { editToken } = useParams(); // optional, for continuing edit
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledProjectId = searchParams.get("project_id");

  const [form, setForm] = useState(emptyForm);
  const [employees, setEmployees] = useState([]);
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressSel, setProgressSel] = useState(() => new Set());
  const [result, setResult] = useState(null); // { number, edit_token, sign_token }
  const [copied, setCopied] = useState({ edit: false, sign: false, short: false });
  const [lightbox, setLightbox] = useState(null);

  const t = UNDERLAG_I18N[form.language] || UNDERLAG_I18N.no;

  // Fetch dropdown options + optional draft load
  useEffect(() => {
    (async () => {
      try {
        const [emps, acts, projs] = await Promise.all([
          axios.get(`${API}/underlag/employees`),
          axios.get(`${API}/underlag/activities`),
          axios.get(`${API}/underlag/projects`),
        ]);
        setEmployees((emps.data.employees || []).map((e) => e.name));
        setActivities((acts.data.activities || []).map((a) => a.name));
        const projList = projs.data.projects || [];
        setProjects(projList);
        // If deep-linked from a Doserne project card (?project_id=…),
        // pre-fill address/customer/customer_email so the worker doesn't
        // have to re-pick the project they just came from.
        if (prefilledProjectId && !editToken) {
          const p = projList.find((x) => x.id === prefilledProjectId);
          if (p) {
            setForm((f) => ({
              ...f,
              project_id: p.id,
              address: p.address || "",
              customer: p.customer || "",
              customer_email: p.customer_email || "",
            }));
          }
        }
      } catch (e) {
        console.error("Failed to load lists", e);
      }
      if (editToken) {
        try {
          const { data } = await axios.get(`${API}/underlag/edit/${editToken}`);
          setForm({ ...emptyForm(), ...data });
        } catch (e) {
          toast.error("Draft not found");
        }
      }
    })();
  }, [editToken, prefilledProjectId]);

  const updateWorker = (idx, patch) => {
    setForm((f) => ({
      ...f,
      workers: f.workers.map((w, i) => (i === idx ? { ...w, ...patch } : w)),
    }));
  };

  const addWorker = () => {
    setForm((f) => ({ ...f, workers: [...f.workers, emptyWorker()] }));
  };

  const removeWorker = (idx) => {
    setForm((f) => ({ ...f, workers: f.workers.filter((_, i) => i !== idx) }));
  };

  const applySameAsAbove = (idx) => {
    if (idx === 0) return toast.info("Ingen rad over / no row above");
    const above = form.workers[idx - 1];
    updateWorker(idx, {
      job: above.job,
      description: above.description,
      from_h: above.from_h,
      from_m: above.from_m,
      to_h: above.to_h,
      to_m: above.to_m,
      timetype: above.timetype,
      minus_lunch: above.minus_lunch,
    });
  };

  // Client-side image downscale — Facebook / iPhone photos are 5-10MB and hit
  // Cloudinary's free-tier 10MB limit. Downscale to max 1920px longest side,
  // JPEG q=85. Only runs for images > 2MB. Non-images pass through untouched.
  const compressImageIfNeeded = async (file) => {
    if (!file.type.startsWith("image/") || file.size < 2 * 1024 * 1024) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const MAX = 1920;
      const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob || blob.size >= file.size) return file; // no gain → keep original
      return new File([blob], file.name.replace(/\.(heic|heif|png|webp)$/i, ".jpg"), {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch (e) {
      console.warn("Image compress failed, uploading original:", e);
      return file;
    }
  };

  const onFileSelected = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    // Guard: reject files > 20MB to prevent silent Cloudinary rejections
    const MAX_SIZE = 20 * 1024 * 1024;
    const oversized = arr.filter((f) => f.size > MAX_SIZE);
    if (oversized.length) {
      toast.error(
        `File(s) too big (max 20MB): ${oversized.map((f) => f.name).join(", ")}`
      );
      return;
    }
    setUploadingCount((n) => n + arr.length);
    try {
      // Get signature
      const { data: sig } = await axios.get(`${API}/cloudinary/signature`, {
        params: { resource_type: "image", folder: "uploads/underlag" },
      });
      for (const rawFile of arr) {
        const file = await compressImageIfNeeded(rawFile);
        try {
          const isImage = file.type.startsWith("image/");
          const resourceType = isImage ? "image" : "raw";
          const uploadUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`;
          const fd = new FormData();
          fd.append("file", file);
          fd.append("api_key", sig.api_key);
          fd.append("timestamp", sig.timestamp);
          fd.append("signature", sig.signature);
          fd.append("folder", sig.folder);
          const { data: up } = await axios.post(uploadUrl, fd);
          setForm((f) => ({
            ...f,
            attachments: [
              ...f.attachments,
              { url: up.secure_url, name: file.name, type: isImage ? "image" : "raw" },
            ],
          }));
        } catch (e) {
          // Surface actual Cloudinary error so we can diagnose (file too big, invalid format, etc.)
          const cldErr = e?.response?.data?.error?.message;
          const status = e?.response?.status;
          console.error("Upload failed", { file: file.name, size: file.size, type: file.type, status, cldErr, e });
          toast.error(
            cldErr
              ? `Upload failed (${file.name}): ${cldErr}`
              : `Upload failed: ${file.name}${status ? ` (HTTP ${status})` : ""}`
          );
        } finally {
          setUploadingCount((n) => Math.max(0, n - 1));
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Cloudinary signature failed");
      setUploadingCount(0);
    }
  };

  const removeAttachment = (idx) => {
    setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const openProgressPicker = async () => {
    if (!form.project_id) { toast.error(t.selectProjectFirst); return; }
    setProgressOpen(true);
    setProgressLoading(true);
    setProgressSel(new Set());
    try {
      const { data } = await axios.get(`${API}/underlag/project-progress-photos`, { params: { project_id: form.project_id } });
      setProgressPhotos(data.photos || []);
    } catch {
      toast.error("Error");
      setProgressPhotos([]);
    }
    setProgressLoading(false);
  };

  const toggleProgressSel = (url) => setProgressSel((s) => {
    const n = new Set(s);
    if (n.has(url)) n.delete(url); else n.add(url);
    return n;
  });

  const addProgressSelected = () => {
    const count = progressSel.size;
    setForm((f) => {
      const existing = new Set((f.attachments || []).map((a) => a.url));
      const toAdd = progressPhotos
        .filter((p) => progressSel.has(p.url) && !existing.has(p.url))
        .map((p) => ({ url: p.url, name: p.name || `progress-${p.date || ""}`, type: "image" }));
      return { ...f, attachments: [...f.attachments, ...toAdd] };
    });
    setProgressOpen(false);
    if (count) toast.success(`+${count}`);
  };

  const canSave = () => {
    if (!form.address.trim()) return false;
    if (!form.customer.trim()) return false;
    if (!form.worker_email.trim()) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.worker_email.trim())) return false;
    return true;
  };

  const handleSave = async () => {
    if (!canSave()) {
      toast.error("Fill in required fields (marked *)");
      return;
    }
    setSaving(true);
    try {
      let response;
      if (editToken) {
        await axios.put(`${API}/underlag/edit/${editToken}`, form);
        // We also need the sign_token + slug — refetch
        const { data } = await axios.get(`${API}/underlag/edit/${editToken}`);
        response = { number: data.number, edit_token: editToken, sign_token: data.sign_token, slug: data.slug };
      } else {
        const { data } = await axios.post(`${API}/underlag/create`, form);
        response = data;
      }
      setResult(response);
      toast.success("Saved!");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((c) => ({ ...c, [key]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const origin = window.location.origin;

  // Signed underlags open read-only from the project detail page. We use a
  // native <fieldset disabled> below to freeze every form control at once,
  // and hide the Save / Submit buttons.
  const readOnly = form.status === "signed";

  // Back button: return to the PROJECT the underlag belongs to (opened from the
  // project detail page, usually in a new tab), else to Doserne home.
  const backProjectId = form.project_id || prefilledProjectId || "";
  const backTo = backProjectId ? `/doserne/prosjekter/${backProjectId}` : "/doserne";
  const backLabel = backProjectId ? "← Prosjekt" : "← Doserne";

  return (
    <div className="light-theme min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16 text-slate-900">
      {/* Header */}
      <div className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={backTo}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest bg-slate-800 hover:bg-slate-700 border border-slate-700 whitespace-nowrap"
              data-testid="back-to-doserne"
              style={{ color: "#fff" }}
              title={backProjectId ? "Tilbake til prosjektet" : "Tilbake til Doserne Academy"}
            >
              {backLabel}
            </Link>
            <div className="min-w-0">
              <div className="text-lg sm:text-2xl font-bold tracking-tight truncate">
                Doserne <span className="text-cyan-400">Stillas</span>
                <span className="text-slate-400 text-sm sm:text-base ml-1">AS</span>
              </div>
              <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5 truncate">{t.subtitle}</div>
            </div>
          </div>
          <select
            value={form.language}
            onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs sm:text-sm flex-shrink-0"
            data-testid="lang-selector"
          >
            <option value="en">🇬🇧 EN</option>
            <option value="no">🇳🇴 NO</option>
            <option value="ro">🇷🇴 RO</option>
            <option value="pl">🇵🇱 PL</option>
          </select>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-900" data-testid="form-title">
          {t.formTitle}
        </h1>

        {readOnly && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="underlag-readonly-banner">
            <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
              ✓
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black text-emerald-900">Underlag signert — visning kun for lesing</div>
              <div className="text-xs text-emerald-700 mt-0.5">Denne underlagen er allerede signert av kunden og kan ikke redigeres.</div>
            </div>
            {form.number != null && (
              <div className="px-2 py-1 rounded-lg font-mono font-black text-xs bg-emerald-100 text-emerald-800 border border-emerald-200 flex-shrink-0">
                #{form.number}
              </div>
            )}
          </div>
        )}

        <fieldset disabled={readOnly} className={`space-y-6 ${readOnly ? "opacity-90" : ""}`} style={readOnly ? { pointerEvents: "auto" } : {}}>
        {/* Step 1: Project selector — always visible at top */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-cyan-300 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-cyan-500 text-white flex items-center justify-center font-black text-sm shadow-sm" style={{ color: "#fff" }}>1</div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-bold text-slate-900 mb-1">{t.project}</label>
              {projects.length > 0 ? (
                <>
                  <ProjectSelect
                    projects={projects}
                    placeholder={t.projectNone}
                    hint={t.projectHint}
                    onPick={(proj) => {
                      setForm((f) => ({
                        ...f,
                        project_id: proj.id || "",
                        address: proj.address || "",
                        customer: proj.customer || "",
                        customer_email: proj.customer_email || "",
                      }));
                      toast.success(proj.address);
                    }}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-500 italic">Ingen prosjekter i katalogen ennå. Fyll ut manuelt under, eller <a href="/admin/underlag" className="text-cyan-600 underline">legg til prosjekter fra admin</a>.</p>
              )}
            </div>
          </div>
        </div>

        {/* Section 1: Basic */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          {(() => {
            const nwr = nonWorkingReason(form.date);
            if (!nwr) return null;
            const label = (NON_WORKING_LABELS[form.language] || NON_WORKING_LABELS.no)[nwr];
            return (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3 text-amber-900 text-sm font-semibold" data-testid="non-working-banner">
                {label}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t.date} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-date"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t.address} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder={t.addressPh}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-address"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t.customer} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.customer}
                onChange={(e) => setForm((f) => ({ ...f, customer: e.target.value }))}
                placeholder={t.customerPh}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none resize-none"
                data-testid="input-customer"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t.customerEmail}
              </label>
              <input
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))}
                placeholder="klient@firma.no"
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-customer-email"
              />
              <p className="text-xs text-slate-500 mt-1">{t.customerEmailHint}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">{t.workDesc}</label>
            <textarea
              value={form.work_description}
              onChange={(e) => setForm((f) => ({ ...f, work_description: e.target.value }))}
              placeholder={t.workDescPh}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              data-testid="input-workdesc"
            />
          </div>
        </div>

        {/* Section 2: Workers */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">{t.workers}</h2>
          </div>

          {form.workers.map((w, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2"
              data-testid={`worker-row-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-cyan-700">#{idx + 1}</span>
                <div className="flex items-center gap-2">
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => applySameAsAbove(idx)}
                      className="flex items-center gap-1 px-2 py-1 bg-cyan-100 hover:bg-cyan-200 text-cyan-800 text-xs font-semibold rounded-md border border-cyan-300 shadow-sm transition"
                      data-testid={`same-as-above-${idx}`}
                      title={t.sameAsAbove}
                    >
                      <ArrowUp className="w-3.5 h-3.5" /> {t.sameAsAbove}
                    </button>
                  )}
                  {form.workers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeWorker(idx)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`remove-worker-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500 mb-0.5 block">{t.name}</label>
                  <Combobox
                    value={w.name}
                    onChange={(v) => updateWorker(idx, { name: v })}
                    options={employees}
                    placeholder={t.name}
                    testid={`worker-name-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-0.5 block">{t.job}</label>
                  <Combobox
                    value={w.job}
                    onChange={(v) => updateWorker(idx, { job: v })}
                    options={activities}
                    placeholder={t.job}
                    testid={`worker-job-${idx}`}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-500 mb-0.5 block">{t.description}</label>
                <input
                  type="text"
                  value={w.description}
                  onChange={(e) => updateWorker(idx, { description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                  data-testid={`worker-desc-${idx}`}
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-xs text-slate-500 mb-0.5 block">{t.from}</label>
                  <div className="flex gap-1">
                    <select
                      value={w.from_h}
                      onChange={(e) => updateWorker(idx, { from_h: e.target.value })}
                      className="flex-1 px-2 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md text-sm"
                      data-testid={`worker-from-h-${idx}`}
                    >
                      {HOURS.map((h) => (<option key={h} value={h}>{h}</option>))}
                    </select>
                    <span className="self-center">:</span>
                    <select
                      value={w.from_m}
                      onChange={(e) => updateWorker(idx, { from_m: e.target.value })}
                      className="flex-1 px-2 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md text-sm"
                      data-testid={`worker-from-m-${idx}`}
                    >
                      {MINUTES.map((m) => (<option key={m} value={m}>{m}</option>))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-0.5 block">{t.to}</label>
                  <div className="flex gap-1">
                    <select
                      value={w.to_h}
                      onChange={(e) => updateWorker(idx, { to_h: e.target.value })}
                      className="flex-1 px-2 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md text-sm"
                      data-testid={`worker-to-h-${idx}`}
                    >
                      {HOURS.map((h) => (<option key={h} value={h}>{h}</option>))}
                    </select>
                    <span className="self-center">:</span>
                    <select
                      value={w.to_m}
                      onChange={(e) => updateWorker(idx, { to_m: e.target.value })}
                      className="flex-1 px-2 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md text-sm"
                      data-testid={`worker-to-m-${idx}`}
                    >
                      {MINUTES.map((m) => (<option key={m} value={m}>{m}</option>))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-0.5 block">{t.minusLunch}</label>
                  <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md bg-white cursor-pointer h-[42px]">
                    <input
                      type="checkbox"
                      checked={w.minus_lunch}
                      onChange={(e) => updateWorker(idx, { minus_lunch: e.target.checked })}
                      className="w-4 h-4 accent-cyan-500"
                      data-testid={`worker-lunch-${idx}`}
                    />
                    <span className="text-sm">{w.minus_lunch ? "Yes" : "No"}</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-0.5 block">{t.hoursColShort}</label>
                  {(() => {
                    const h = computeHours(w, form.date);
                    return (
                      <div className="flex flex-col justify-center px-2 py-1 bg-cyan-50 border border-cyan-200 rounded-md h-[42px]" data-testid={`worker-hours-${idx}`}>
                        <div className="text-[10px] text-slate-500 leading-tight">
                          <span className="text-cyan-800 font-semibold">N:</span> {fmtH(h.normal)}h
                        </div>
                        <div className="text-[10px] text-slate-500 leading-tight">
                          <span className={`font-semibold ${h.overtime > 0 ? "text-amber-700" : "text-slate-400"}`}>OT:</span> {fmtH(h.overtime)}h
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          ))}

          {/* Add worker — always below last worker */}
          <button
            type="button"
            onClick={addWorker}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white text-sm rounded-md font-semibold shadow-sm transition"
            data-testid="add-worker-btn"
          >
            <Plus className="w-4 h-4" /> {t.addWorker}
          </button>

          {/* Totals row */}
          {(() => {
            const totals = form.workers.reduce(
              (acc, w) => {
                const h = computeHours(w, form.date);
                return { normal: acc.normal + h.normal, overtime: acc.overtime + h.overtime };
              },
              { normal: 0, overtime: 0 }
            );
            return (
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid="totals-row">
                <div className="bg-cyan-500/10 border-2 border-cyan-500/40 rounded-lg p-3 text-center">
                  <div className="text-xs font-semibold text-cyan-800 uppercase tracking-wide">{t.totalNormal}</div>
                  <div className="text-2xl sm:text-3xl font-bold text-cyan-700" data-testid="total-normal">{fmtH(totals.normal)}<span className="text-base font-normal ml-1">h</span></div>
                </div>
                <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-lg p-3 text-center">
                  <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">{t.totalOvertime}</div>
                  <div className="text-2xl sm:text-3xl font-bold text-amber-700" data-testid="total-overtime">{fmtH(totals.overtime)}<span className="text-base font-normal ml-1">h</span></div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Section 3: Extras */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t.toll}</label>
              <input
                type="text"
                value={form.toll_crossings}
                onChange={(e) => setForm((f) => ({ ...f, toll_crossings: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-toll"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t.parking}</label>
              <input
                type="text"
                value={form.parking}
                onChange={(e) => setForm((f) => ({ ...f, parking: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-parking"
              />
            </div>
          </div>

          {/* Materials used */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-slate-700">
                {t.materialsUsedQuestion}
              </label>
              <div className="flex gap-1 rounded-md border border-slate-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, materials_used: false }))}
                  className={`px-3 py-1.5 text-sm font-medium transition ${
                    !form.materials_used ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid="materials-used-no"
                >
                  {t.materialsUsedNo}
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, materials_used: true }))}
                  className={`px-3 py-1.5 text-sm font-medium transition ${
                    form.materials_used ? "bg-cyan-500 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid="materials-used-yes"
                >
                  {t.materialsUsedYes}
                </button>
              </div>
            </div>
            {form.materials_used && (
              <div className="mt-3 p-3 rounded-lg bg-cyan-50 border border-cyan-200 space-y-3" data-testid="materials-details">
                {/* Nett group */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-cyan-800 mb-2">Nett</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">{t.materialsNetRolls}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.materials_net_rolls}
                        onChange={(e) => setForm((f) => ({ ...f, materials_net_rolls: e.target.value }))}
                        placeholder={t.materialsNetRollsPh}
                        className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        data-testid="input-materials-net-rolls"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">{t.materialsNetTies}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.materials_net_ties}
                        onChange={(e) => setForm((f) => ({ ...f, materials_net_ties: e.target.value }))}
                        placeholder={t.materialsNetTiesPh}
                        className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        data-testid="input-materials-net-ties"
                      />
                    </div>
                  </div>
                </div>
                {/* Plast group */}
                <div className="pt-3 border-t border-cyan-200">
                  <div className="text-xs font-bold uppercase tracking-wide text-cyan-800 mb-2">Plast</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">{t.materialsPlastRolls}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.materials_plastic_rolls}
                        onChange={(e) => setForm((f) => ({ ...f, materials_plastic_rolls: e.target.value }))}
                        placeholder={t.materialsPlastRollsPh}
                        className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        data-testid="input-materials-plastic-rolls"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">{t.materialsPlastTies}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.materials_plastic_ties}
                        onChange={(e) => setForm((f) => ({ ...f, materials_plastic_ties: e.target.value }))}
                        placeholder={t.materialsPlastTiesPh}
                        className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        data-testid="input-materials-plastic-ties"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">{t.attachments}</label>
            <label
              className="border-2 border-dashed border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition"
              data-testid="upload-drop-zone"
            >
              <Upload className="w-8 h-8 text-slate-400 mb-2" />
              <span className="text-sm text-slate-600 text-center">{t.uploadHint}</span>
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                onChange={(e) => onFileSelected(e.target.files)}
                className="hidden"
                data-testid="upload-input"
              />
              {uploadingCount > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs text-cyan-700">
                  <Loader2 className="w-3 h-3 animate-spin" /> Uploading {uploadingCount}...
                </div>
              )}
            </label>

            <button
              type="button"
              onClick={openProgressPicker}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-cyan-300 bg-cyan-50 text-cyan-800 text-sm font-bold hover:bg-cyan-100 transition"
              data-testid="import-progress-btn"
            >
              <Images className="w-4 h-4" /> {t.importProgress}
            </button>

            {form.attachments.length > 0 && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="relative group border border-slate-200 rounded-lg overflow-hidden bg-slate-50" data-testid={`attachment-${i}`}>
                    {a.type === "image" ? (
                      <button
                        type="button"
                        onClick={() => setLightbox({ url: a.url, name: a.name })}
                        className="w-full block"
                        data-testid={`attachment-thumb-${i}`}
                      >
                        <img src={a.url} alt={a.name || `attachment-${i}`} className="w-full h-24 object-cover hover:opacity-80 transition" />
                      </button>
                    ) : (
                      <div className="w-full h-24 flex items-center justify-center bg-slate-100 text-xs text-slate-600 p-2 text-center break-all">
                        📄 {a.name}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-80 hover:opacity-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Import from daily progress modal */}
        {progressOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setProgressOpen(false)} data-testid="progress-picker-modal">
            <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <div className="font-black text-slate-900 flex items-center gap-2"><Images className="w-4 h-4" /> {t.importProgressTitle}</div>
                <button type="button" onClick={() => setProgressOpen(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {progressLoading ? (
                  <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : progressPhotos.length === 0 ? (
                  <div className="py-12 text-center text-sm text-slate-400 italic">{t.noProgressPhotos}</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {progressPhotos.map((p, i) => {
                      const sel = progressSel.has(p.url);
                      return (
                        <button
                          type="button"
                          key={`${p.url}-${i}`}
                          onClick={() => toggleProgressSel(p.url)}
                          className={`relative rounded-lg overflow-hidden border-2 text-left transition ${sel ? "border-cyan-500 ring-2 ring-cyan-300" : "border-slate-200"}`}
                          data-testid={`progress-photo-${i}`}
                        >
                          <img src={p.url} alt={p.name || `progress-${i}`} className="w-full h-28 object-cover" />
                          {sel && (
                            <div className="absolute top-1 right-1 bg-cyan-500 text-white rounded-full p-0.5">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          )}
                          <div className="px-2 py-1 text-[10px] text-slate-500 bg-white truncate">
                            {p.date}{p.job ? ` · ${p.job}` : ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="border-t border-slate-200 p-3">
                <button
                  type="button"
                  onClick={addProgressSelected}
                  disabled={progressSel.size === 0}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-black disabled:opacity-50"
                  data-testid="progress-add-selected"
                >
                  <Plus className="w-4 h-4" /> {t.addSelected} {progressSel.size > 0 ? `(${progressSel.size})` : ""}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Lightbox modal for attachments */}
        {lightbox && (
          <div
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-2 sm:p-6"
            onClick={() => setLightbox(null)}
            data-testid="form-lightbox-modal"
          >
            <button
              type="button"
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-slate-900 rounded-full p-2 shadow-lg"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              aria-label="Close"
              data-testid="form-lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={lightbox.url}
              alt={lightbox.name || "preview"}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Section 4: Emails */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              {t.workerEmail} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.worker_email}
              onChange={(e) => setForm((f) => ({ ...f, worker_email: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              data-testid="input-worker-email"
            />
            <p className="text-xs text-slate-500 mt-1">{t.workerEmailHint}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t.ccEmail1}</label>
              <input
                type="email"
                value={form.cc_email_1}
                onChange={(e) => setForm((f) => ({ ...f, cc_email_1: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-cc-1"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t.ccEmail2}</label>
              <input
                type="email"
                value={form.cc_email_2}
                onChange={(e) => setForm((f) => ({ ...f, cc_email_2: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                data-testid="input-cc-2"
              />
            </div>
          </div>
        </div>

        {/* Save button */}
        {!readOnly && (
        <div className="sticky bottom-0 -mx-3 sm:mx-0 bg-white/95 backdrop-blur border-t border-slate-200 p-4 sm:rounded-xl sm:border sm:shadow-lg">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !canSave() || uploadingCount > 0}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-bold text-base sm:text-lg rounded-lg shadow-md transition"
            data-testid="save-btn"
          >
            {saving ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t.saving}</>) : t.save}
          </button>
          <p className="text-xs text-slate-500 mt-2 text-center">{t.saveHint}</p>
        </div>
        )}

        {/* Result modal */}
        {result && (
          <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" data-testid="result-modal">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {t.number} #{result.number}
                  </h3>
                  <p className="text-xs text-slate-500">{t.saveHint}</p>
                </div>
              </div>

              <div className="space-y-3">
                <LinkBox
                  label={t.editLink}
                  url={`${origin}/underlag/edit/${result.edit_token}`}
                  onCopy={(u) => copy(u, "edit")}
                  copied={copied.edit}
                  copyLabel={t.copyLink}
                  copiedLabel={t.copied}
                />
                <LinkBox
                  label={t.signLink}
                  url={`${origin}/underlag/sign/${result.sign_token}`}
                  onCopy={(u) => copy(u, "sign")}
                  copied={copied.sign}
                  copyLabel={t.copyLink}
                  copiedLabel={t.copied}
                  highlight
                />
                {result.slug && (
                  <SlugEditor
                    origin={origin}
                    initialSlug={result.slug}
                    editToken={result.edit_token}
                    onCopy={(u) => copy(u, "short")}
                    copied={copied.short}
                    copyLabel={t.copyLink}
                    copiedLabel={t.copied}
                    onSaved={(newSlug) => setResult((r) => ({ ...r, slug: newSlug }))}
                  />
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    // Continue editing in current tab (this same page becomes edit context)
                    navigate(`/underlag/edit/${result.edit_token}`, { replace: true });
                    setResult(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-md text-sm hover:bg-slate-50"
                  data-testid="continue-edit-btn"
                >
                  {t.editLink}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.open(`/underlag/sign/${result.sign_token}`, "_blank");
                  }}
                  className="flex-1 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md text-sm font-medium"
                  data-testid="open-sign-btn"
                >
                  {t.signLink} →
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setForm(emptyForm());
                  setResult(null);
                  setCopied({ edit: false, sign: false, short: false });
                  navigate("/underlag", { replace: true });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="w-full mt-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-sm font-semibold flex items-center justify-center gap-2"
                data-testid="start-new-btn"
              >
                <Plus className="w-4 h-4" /> {t.startNew}
              </button>
            </div>
          </div>
        )}
        </fieldset>
      </div>
    </div>
  );
}

function LinkBox({ label, url, onCopy, copied, copyLabel, copiedLabel, highlight }) {
  return (
    <div className={`border rounded-lg p-3 ${highlight ? "border-cyan-400 bg-cyan-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      <div className="flex gap-2 items-center">
        <input
          type="text"
          readOnly
          value={url}
          className="flex-1 px-2 py-1.5 text-xs bg-white border border-slate-200 rounded font-mono truncate"
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          onClick={() => onCopy(url)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium ${
            copied ? "bg-emerald-500 text-white" : "bg-slate-900 text-white hover:bg-slate-800"
          }`}
        >
          {copied ? (<><CheckCircle2 className="w-3 h-3" /> {copiedLabel}</>) : (<><Copy className="w-3 h-3" /> {copyLabel}</>)}
        </button>
      </div>
    </div>
  );
}


/**
 * SlugEditor — friendly short-URL box.
 * Shows /u/<slug>, lets the owner edit and save a new slug (persisted server-side).
 */
function SlugEditor({ origin, initialSlug, editToken, onCopy, copied, copyLabel, copiedLabel, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(initialSlug);
  const [busy, setBusy]       = useState(false);

  const shortUrl = `${origin}/u/${initialSlug}`;

  const save = async () => {
    const clean = draft.trim().toLowerCase();
    if (clean === initialSlug) { setEditing(false); return; }
    if (clean.length < 3) { toast.error("Min 3 caractere"); return; }
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/underlag/edit/${editToken}/slug`, { slug: clean });
      onSaved(data.slug);
      toast.success("Link scurt actualizat");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    }
    setBusy(false);
  };

  return (
    <div className="border-2 border-emerald-400 bg-emerald-50 rounded-lg p-3" data-testid="slug-editor">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Link scurt</div>
        {!editing ? (
          <button
            type="button"
            onClick={() => { setDraft(initialSlug); setEditing(true); }}
            className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-900"
            data-testid="slug-edit-btn"
          >Redenumește</button>
        ) : (
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setEditing(false)}
                    className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900">Anulează</button>
            <button type="button" onClick={save} disabled={busy}
                    className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-900 disabled:opacity-40"
                    data-testid="slug-save-btn">
              {busy ? "…" : "Salvează"}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="flex gap-2 items-center">
          <input
            type="text" readOnly value={shortUrl}
            onFocus={(e) => e.target.select()}
            className="flex-1 px-2 py-1.5 text-xs bg-white border border-emerald-200 rounded font-mono text-emerald-900 truncate"
          />
          <button
            type="button"
            onClick={() => onCopy(shortUrl)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium ${
              copied ? "bg-emerald-600 text-white" : "bg-emerald-700 text-white hover:bg-emerald-800"
            }`}
            data-testid="slug-copy-btn"
          >
            {copied ? (<><CheckCircle2 className="w-3 h-3" /> {copiedLabel}</>) : (<><Copy className="w-3 h-3" /> {copyLabel}</>)}
          </button>
        </div>
      ) : (
        <div>
          <div className="flex gap-1 items-center">
            <span className="text-xs text-slate-500 font-mono">{origin}/u/</span>
            <input
              type="text" autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-"))}
              placeholder="klofta-mar18"
              className="flex-1 px-2 py-1.5 text-xs bg-white border border-emerald-300 rounded font-mono text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="slug-input"
            />
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Doar litere mici, cifre și cratime · min 3 caractere</div>
        </div>
      )}
    </div>
  );
}
